import { useState, useEffect } from "react";
import { GoogleAuthProvider, OAuthProvider, signInWithPopup } from "firebase/auth";
import { auth } from "../firebase";
import { requestDeviceBoundMagicLink } from "../auth/magicLink";

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,600;0,700;1,400;1,600&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg: #f0f4ee;
    --bg2: #e4ebe1;
    --ink: #1a2016;
    --ink-muted: #5a6456;
    --accent: #3d6b35;
    --accent-hover: #2e5228;
    --accent-light: #6aaa5e;
    --accent-pale: #d4e8d0;
    --card: #ffffff;
    --border: #d4dfd0;
    --shadow-lg: 0 16px 56px rgba(26,32,22,0.13);
  }

  .bd-root {
    font-family: 'DM Sans', sans-serif;
    background: var(--bg);
    color: var(--ink);
    min-height: 100vh;
    overflow-x: hidden;
    position: relative;
  }

  .bd-root::before {
    content: '';
    position: fixed;
    inset: 0;
    background-image: radial-gradient(circle, #a8c4a2 1px, transparent 1px);
    background-size: 26px 26px;
    opacity: 0.3;
    pointer-events: none;
    z-index: 0;
  }

  .bd-orb {
    position: fixed;
    border-radius: 50%;
    filter: blur(80px);
    opacity: 0.18;
    pointer-events: none;
    z-index: 0;
  }
  .bd-orb-1 { width: 500px; height: 500px; background: radial-gradient(circle, #6aaa5e, transparent); top: -100px; left: -100px; }
  .bd-orb-2 { width: 400px; height: 400px; background: radial-gradient(circle, #b8d8a0, transparent); bottom: -80px; right: -80px; }

  .bd-nav {
    position: relative;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 52px;
    border-bottom: 1px solid rgba(212,223,208,0.5);
  }

  .bd-nav-logo {
    font-family: 'Lora', serif;
    font-weight: 700;
    font-size: 1.1rem;
    letter-spacing: -0.02em;
    color: var(--ink);
  }
  .bd-nav-logo span { color: var(--accent); }

  .bd-nav-links { display: flex; gap: 36px; list-style: none; }
  .bd-nav-links a {
    text-decoration: none;
    font-size: 0.85rem;
    color: var(--ink-muted);
    transition: color 0.2s;
  }
  .bd-nav-links a:hover { color: var(--accent); }

  .bd-hero {
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: 1fr 420px;
    gap: 56px;
    align-items: start;
    max-width: 1180px;
    margin: 0 auto;
    padding: 60px 52px 80px;
  }

  .bd-left { display: flex; flex-direction: column; }

  .bd-quote-banner {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    background: var(--ink);
    color: white;
    padding: 16px 20px;
    border-radius: 14px;
    margin-bottom: 36px;
    opacity: 0;
    transform: translateY(14px);
    transition: opacity 0.5s ease, transform 0.5s ease;
  }
  .bd-quote-banner.vis { opacity: 1; transform: translateY(0); }

  .bd-quote-mark {
    font-family: 'Lora', serif;
    font-size: 3rem;
    line-height: 0.7;
    color: var(--accent-light);
    flex-shrink: 0;
    margin-top: 4px;
  }

  .bd-quote-text {
    font-family: 'Lora', serif;
    font-style: italic;
    font-size: 0.93rem;
    line-height: 1.6;
    color: #e8f0e5;
  }

  .bd-quote-attr {
    font-size: 0.72rem;
    color: #8aab85;
    margin-top: 6px;
    letter-spacing: 0.04em;
  }

  .bd-h1 {
    font-family: 'Lora', serif;
    font-weight: 700;
    font-size: clamp(2.4rem, 4vw, 3.4rem);
    line-height: 1.1;
    letter-spacing: -0.025em;
    color: var(--ink);
    margin-bottom: 18px;
    opacity: 0;
    transform: translateY(14px);
    transition: opacity 0.5s 0.15s ease, transform 0.5s 0.15s ease;
  }
  .bd-h1.vis { opacity: 1; transform: translateY(0); }
  .bd-h1 em { font-style: italic; color: var(--accent); }

  .bd-subtitle {
    font-size: 1rem;
    line-height: 1.7;
    color: var(--ink-muted);
    max-width: 480px;
    font-weight: 300;
    opacity: 0;
    transform: translateY(14px);
    transition: opacity 0.5s 0.25s ease, transform 0.5s 0.25s ease;
  }
  .bd-subtitle.vis { opacity: 1; transform: translateY(0); }

  .bd-auth {
    background: var(--card);
    border: 1px solid var(--border);
    border-radius: 22px;
    padding: 38px 34px;
    box-shadow: var(--shadow-lg);
    position: sticky;
    top: 28px;
    opacity: 0;
    transform: translateY(14px);
    transition: opacity 0.55s 0.2s ease, transform 0.55s 0.2s ease;
  }
  .bd-auth.vis { opacity: 1; transform: translateY(0); }

  .bd-auth-title {
    text-align: center;
    font-family: 'Lora', serif;
    font-size: 1.15rem;
    font-weight: 600;
    color: var(--ink);
    margin-bottom: 24px;
    letter-spacing: -0.01em;
  }

  .bd-input-wrap { position: relative; margin-bottom: 12px; }

  .bd-input-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #aab8a5;
    pointer-events: none;
    display: flex;
    align-items: center;
  }

  .bd-input {
    width: 100%;
    padding: 12px 14px 12px 38px;
    border: 1.5px solid var(--border);
    border-radius: 11px;
    font-family: 'DM Sans', sans-serif;
    font-size: 0.875rem;
    color: var(--ink);
    background: #fafcf9;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
  }
  .bd-input:focus {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(61,107,53,0.13);
  }
  .bd-input::placeholder { color: #b0c0ab; }

  .bd-btn-primary {
    width: 100%;
    padding: 13px;
    background: var(--accent);
    color: white;
    border: none;
    border-radius: 11px;
    font-family: 'DM Sans', sans-serif;
    font-size: 0.82rem;
    font-weight: 500;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    cursor: pointer;
    transition: background 0.2s, box-shadow 0.2s, transform 0.1s;
    margin-bottom: 20px;
  }
  .bd-btn-primary:hover {
    background: var(--accent-hover);
    box-shadow: 0 6px 20px rgba(61,107,53,0.32);
  }
  .bd-btn-primary:active { transform: scale(0.99); }
  .bd-btn-primary:disabled {
    background: var(--accent-pale);
    color: var(--ink-muted);
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  .bd-divider {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
  }
  .bd-divider-line { flex: 1; height: 1px; background: var(--border); }
  .bd-divider-text { font-size: 0.73rem; color: var(--ink-muted); }

  .bd-sso-list { display: flex; flex-direction: column; gap: 10px; }

  .bd-sso-btn {
    width: 100%;
    padding: 12px 16px;
    background: white;
    border: 1.5px solid var(--border);
    border-radius: 11px;
    font-family: 'DM Sans', sans-serif;
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--ink);
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 12px;
    transition: border-color 0.2s, box-shadow 0.2s;
  }
  .bd-sso-btn:hover {
    border-color: #b8c8b4;
    box-shadow: 0 2px 8px rgba(26,32,22,0.07);
  }

  .bd-success-message {
    background: var(--accent-pale);
    color: var(--ink);
    padding: 16px;
    border-radius: 11px;
    margin-bottom: 20px;
    font-family: 'DM Sans', sans-serif;
    font-size: 0.875rem;
    line-height: 1.5;
  }

  .bd-success-message strong {
    display: block;
    margin-bottom: 4px;
    font-weight: 600;
  }

  .bd-secondary-actions {
    display: flex;
    gap: 12px;
    margin-top: 16px;
  }

  .bd-btn-secondary {
    flex: 1;
    padding: 10px;
    background: transparent;
    color: var(--accent);
    border: 1.5px solid var(--accent);
    border-radius: 11px;
    font-family: 'DM Sans', sans-serif;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s, color 0.2s;
  }
  .bd-btn-secondary:hover {
    background: var(--accent);
    color: white;
  }

  @media (max-width: 860px) {
    .bd-nav { padding: 16px 24px; }
    .bd-nav-links { display: none; }
    .bd-hero { grid-template-columns: 1fr; padding: 28px 24px 60px; gap: 40px; }
    .bd-auth { position: static; }
  }
`;

const MailIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2"/><polyline points="2,4 12,13 22,4"/>
  </svg>
);

const GoogleLogo = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" style={{flexShrink:0}}>
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const MicrosoftLogo = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" style={{flexShrink:0}}>
    <rect x="1" y="1" width="10.5" height="10.5" fill="#F25022"/>
    <rect x="12.5" y="1" width="10.5" height="10.5" fill="#7FBA00"/>
    <rect x="1" y="12.5" width="10.5" height="10.5" fill="#00A4EF"/>
    <rect x="12.5" y="12.5" width="10.5" height="10.5" fill="#FFB900"/>
  </svg>
);

const AppleLogo = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style={{flexShrink:0}}>
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98l-.09.06c-.22.15-2.18 1.27-2.16 3.8.03 3.02 2.65 4.03 2.68 4.04l-.07.28zM13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

export default function Login() {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const [error, setError] = useState("");
  const [visible, setVisible] = useState(false);

  const emailOk = isValidEmail(email);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  // Check if user is already authenticated and redirect
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      console.log("Login component - auth state changed:", user);
      if (user) {
        console.log("User already authenticated, redirecting to app...");
        window.location.href = '/';
      }
    });
    return unsubscribe;
  }, []);

  async function onSendLink() {
    setError("");
    const trimmed = email.trim();

    if (!isValidEmail(trimmed)) {
      setError("Please enter a valid email address.");
      return;
    }

    try {
      setSending(true);
      console.log("Sending magic link to:", trimmed);
      console.log("Environment check:", {
        hasApiKey: !!import.meta.env.VITE_FIREBASE_API_KEY,
        hasProjectId: !!import.meta.env.VITE_FIREBASE_PROJECT_ID,
      });
      
      await requestDeviceBoundMagicLink(trimmed);
      setSentTo(trimmed);
      console.log("Magic link sent successfully");
    } catch (err) {
      console.error("Magic link error:", err);
      setError("Failed to send magic link. Please try again.");
    } finally {
      setSending(false);
    }
  }

  async function onGoogle() {
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    } catch (err) {
      console.error("Google sign-in error:", err);
      setError("Google sign-in failed. Please try again.");
    }
  }

  async function onMicrosoft() {
    try {
      console.log("Starting Microsoft sign-in...");
      const provider = new OAuthProvider('microsoft.com');
      provider.addScope('email');
      provider.addScope('profile');
      
      const result = await signInWithPopup(auth, provider);
      console.log("Microsoft sign-in successful:", result.user);
      console.log("User email:", result.user.email);
      console.log("User UID:", result.user.uid);
      
      // The onAuthStateChanged listener in App.jsx should handle the redirect
      // But let's give it a moment to process
      setTimeout(() => {
        console.log("Checking auth state after sign-in...");
        const currentUser = auth.currentUser;
        console.log("Current user after timeout:", currentUser);
      }, 1000);
      
    } catch (err) {
      console.error("Microsoft sign-in error:", err);
      setError("Microsoft sign-in failed. Please try again.");
    }
  }

  function notImplemented() {
    setError("That provider isn't wired yet. Use Magic Link, Google, or Microsoft for now.");
  }

  const v = visible ? "vis" : "";

  return (
    <>
      <style>{styles}</style>
      <div className="bd-root">
        <div className="bd-orb bd-orb-1" />
        <div className="bd-orb bd-orb-2" />

        <nav className="bd-nav">
          <div className="bd-nav-logo">Be<span>Deliberate</span></div>
          <ul className="bd-nav-links">
            <li><a href="#">How it works</a></li>
            <li><a href="#">For teams</a></li>
            <li><a href="#">Pricing</a></li>
            <li><a href="#">Blog</a></li>
          </ul>
        </nav>

        <section className="bd-hero">

          <div className="bd-left">
            <div className={`bd-quote-banner ${v}`}>
              <div className="bd-quote-mark">"</div>
              <div>
                <div className="bd-quote-text">
                  Never leave that till tomorrow which you can do today.
                </div>
                <div className="bd-quote-attr">— Benjamin Franklin</div>
              </div>
            </div>

            <h1 className={`bd-h1 ${v}`}>
              Make every<br />hour <em>count.</em>
            </h1>

            <p className={`bd-subtitle ${v}`}>
              BeDeliberate helps you plan your time, track how you actually use it,
              and build the habits that turn intentions into results — whether you're
              a student, professional, or anyone who wants to do more with their day.
            </p>
          </div>

          <div className={`bd-auth ${v}`}>
            <div className="bd-auth-title">Log in to your account below</div>

            {error && (
              <div className="bd-success-message" style={{background: '#fee', color: '#c00', border: '1px solid #fcc'}}>
                {error}
              </div>
            )}

            {sentTo ? (
              <>
                <div className="bd-success-message">
                  <strong>Check your email</strong>
                  We sent a secure, one-time sign-in link to <b>{sentTo}</b>.<br />
                  Open it on <b>this device & browser</b> to continue.
                </div>

                <div className="bd-secondary-actions">
                  <button className="bd-btn-secondary" onClick={onSendLink} disabled={sending}>
                    {sending ? "Resending…" : "Resend link"}
                  </button>
                  <button 
                    className="bd-btn-secondary" 
                    onClick={() => {
                      setSentTo("");
                      setEmail("");
                    }}
                  >
                    Change email
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="bd-input-wrap">
                  <span className="bd-input-icon"><MailIcon /></span>
                  <input
                    id="bd-email"
                    type="email"
                    className="bd-input"
                    placeholder="Email address"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    autoComplete="email"
                  />
                </div>

                <button 
                  className="bd-btn-primary" 
                  onClick={onSendLink}
                  disabled={!emailOk || sending}
                >
                  {sending ? "Sending…" : "Continue"}
                </button>

                <div className="bd-divider">
                  <div className="bd-divider-line" />
                  <span className="bd-divider-text">or continue with</span>
                  <div className="bd-divider-line" />
                </div>

                <div className="bd-sso-list">
                  <button className="bd-sso-btn" onClick={onGoogle}><GoogleLogo /> Google</button>
                  <button className="bd-sso-btn" onClick={onMicrosoft}><MicrosoftLogo /> Microsoft</button>
                  <button className="bd-sso-btn" onClick={notImplemented}><AppleLogo /> Apple</button>
                </div>
              </>
            )}
          </div>

        </section>
      </div>
    </>
  );
}